import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import LoanAcceptance from './LoanAcceptance';
import SignedContract from './SignedContract';
import '../styles/LoanList.css';

function LoaneeLoans() {
  const { currentUser } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showAcceptance, setShowAcceptance] = useState(null);
  const [showContract, setShowContract] = useState(null);
  const [activeTab, setActiveTab] = useState('current');

  useEffect(() => {
    fetchLoans();
  }, [currentUser]);

  async function fetchLoans() {
    try {
      const loansQuery = query(
        collection(db, 'loans'),
        where('loaneeId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(loansQuery);
      const loansList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLoans(loansList);
    } catch (err) {
      console.error('Error fetching loans:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleAcceptanceComplete() {
    setShowAcceptance(null);
    fetchLoans();
  }

  function getStatusBadgeClass(status) {
    switch (status) {
      case 'pending_acceptance':
        return 'status-badge status-pending';
      case 'active':
        return 'status-badge status-active';
      case 'paid_off':
        return 'status-badge status-paid';
      case 'rejected':
        return 'status-badge status-rejected';
      default:
        return 'status-badge';
    }
  }

  function getStatusText(status) {
    switch (status) {
      case 'pending_acceptance':
        return 'Pending Acceptance';
      case 'active':
        return 'Active';
      case 'paid_off':
        return 'Paid Off';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  }

  function getAdjustedAmortizationSchedule(loan) {
    if (!loan.payments || loan.payments.length === 0) {
      return loan.amortizationSchedule || [];
    }

    // Sort payments by date
    const sortedPayments = [...loan.payments].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    let remainingBalance = loan.amount;
    const monthlyRate = loan.interestRate / 100 / 12;
    const adjustedSchedule = [];
    let paymentIndex = 0;

    for (let period = 1; period <= loan.termLength; period++) {
      // Stop if balance is already zero
      if (remainingBalance <= 0.01) {
        break;
      }

      // Check if there's an actual payment for this period
      const actualPayment = sortedPayments[paymentIndex];
      
      if (actualPayment && paymentIndex < sortedPayments.length) {
        // Use actual payment data
        const interestPortion = remainingBalance * monthlyRate;
        const principalPortion = actualPayment.amount - interestPortion;
        remainingBalance = Math.max(0, remainingBalance - principalPortion);

        adjustedSchedule.push({
          period,
          paymentDate: actualPayment.date,
          payment: actualPayment.amount,
          interest: interestPortion,
          principal: principalPortion,
          balance: remainingBalance,
          isPaid: true
        });

        paymentIndex++;
      } else {
        // Use projected payment data
        const originalRow = loan.amortizationSchedule?.[period - 1];
        if (originalRow) {
          // Recalculate based on current remaining balance
          const interestPortion = remainingBalance * monthlyRate;
          
          // Check if this is the final payment (remaining balance + interest < regular payment)
          const totalOwed = remainingBalance + interestPortion;
          let paymentAmount;
          let principalPortion;
          let newBalance;
          
          if (totalOwed < loan.monthlyPayment) {
            // Final payment - only pay what's owed
            paymentAmount = totalOwed;
            principalPortion = remainingBalance;
            newBalance = 0;
          } else {
            // Regular payment
            paymentAmount = loan.monthlyPayment;
            principalPortion = paymentAmount - interestPortion;
            newBalance = Math.max(0, remainingBalance - principalPortion);
          }

          adjustedSchedule.push({
            period,
            paymentDate: originalRow.paymentDate,
            payment: paymentAmount,
            interest: interestPortion,
            principal: principalPortion,
            balance: newBalance,
            isPaid: false
          });

          remainingBalance = newBalance;
        }
      }
    }

    return adjustedSchedule;
  }

  if (loading) {
    return <div className="loading">Loading loans...</div>;
  }

  // Filter loans by tab
  const currentLoans = loans.filter(loan => loan.status === 'active');
  const inProcessLoans = loans.filter(loan => loan.status === 'pending_acceptance' || loan.status === 'rejected');
  const completedLoans = loans.filter(loan => loan.status === 'paid_off');

  // Get loans for current tab
  function getLoansForTab() {
    switch (activeTab) {
      case 'current':
        return currentLoans;
      case 'process':
        return inProcessLoans;
      case 'completed':
        return completedLoans;
      default:
        return [];
    }
  }

  const displayedLoans = getLoansForTab();

  return (
    <div className="loan-list-container">
      <h2>My Loans</h2>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current Loans ({currentLoans.length})
        </button>
        <button
          className={`tab ${activeTab === 'process' ? 'active' : ''}`}
          onClick={() => setActiveTab('process')}
        >
          Loans in Process ({inProcessLoans.length})
        </button>
        <button
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed Loans ({completedLoans.length})
        </button>
      </div>

      {showAcceptance && (
        <div className="modal-overlay" onClick={() => setShowAcceptance(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAcceptance(null)}>×</button>
            <LoanAcceptance
              loan={showAcceptance}
              currentUser={currentUser}
              onComplete={handleAcceptanceComplete}
            />
          </div>
        </div>
      )}

      {showContract && (
        <div className="modal-overlay" onClick={() => setShowContract(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowContract(null)}>×</button>
            <SignedContract
              loan={showContract}
              onClose={() => setShowContract(null)}
            />
          </div>
        </div>
      )}

      {displayedLoans.length === 0 ? (
        <div className="empty-state">
          <p>No loans in this category.</p>
        </div>
      ) : (
        <div className="loan-cards">
          {displayedLoans.map(loan => (
            <div key={loan.id} className="loan-card">
              <div className="loan-card-header">
                <div>
                  <h3>${loan.amount.toFixed(2)}</h3>
                  {(loan.status === 'active' || loan.status === 'paid_off') && loan.loaneeSignature && (
                    <button 
                      className="btn btn-link" 
                      onClick={() => setShowContract(loan)}
                      style={{fontSize: '0.9rem', padding: '0.25rem 0'}}
                    >
                      📄 View Signed Contract
                    </button>
                  )}
                </div>
                <span className={getStatusBadgeClass(loan.status)}>
                  {getStatusText(loan.status)}
                </span>
              </div>

              <div className="loan-card-body">
                <div className="loan-detail">
                  <span className="label">Loaner:</span>
                  <span className="value">{loan.loanerName}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Purpose:</span>
                  <span className="value">{loan.purpose}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Interest Rate:</span>
                  <span className="value">{loan.interestRate}%</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Term:</span>
                  <span className="value">{loan.termLength} {loan.termType}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Monthly Payment:</span>
                  <span className="value">${loan.monthlyPayment.toFixed(2)}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Total Cost of Loan:</span>
                  <span className="value">${(loan.amount + loan.totalInterest).toFixed(2)}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Total Interest:</span>
                  <span className="value">${loan.totalInterest.toFixed(2)}</span>
                </div>
                <div className="loan-detail">
                  <span className="label">Remaining Balance:</span>
                  <span className="value highlight">${loan.remainingBalance.toFixed(2)}</span>
                </div>
                {loan.notes && (
                  <div className="loan-detail">
                    <span className="label">Notes:</span>
                    <span className="value">{loan.notes}</span>
                  </div>
                )}
              </div>

              <div className="loan-card-footer">
                {loan.status === 'pending_acceptance' && (
                  <div className="action-buttons">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAcceptance(loan)}
                    >
                      Review and Sign Agreement
                    </button>
                  </div>
                )}
                <button
                  className="btn btn-link"
                  onClick={() => setSelectedLoan(selectedLoan?.id === loan.id ? null : loan)}
                >
                  {selectedLoan?.id === loan.id ? 'Hide Details' : 'View Details'}
                </button>
              </div>

              {selectedLoan?.id === loan.id && (
                <div className="loan-details-expanded">
                  <h4>Amortization Schedule {loan.payments && loan.payments.length > 0 && '(Adjusted for Payments)'}</h4>
                  <div className="table-wrapper">
                    <table className="amortization-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Payment</th>
                          <th>Principal</th>
                          <th>Interest</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getAdjustedAmortizationSchedule(loan).map((row) => (
                          <tr key={row.period} style={{backgroundColor: row.isPaid ? '#e8f5e0' : 'transparent'}}>
                            <td>{row.isPaid ? '✓ Paid' : 'Pending'}</td>
                            <td>{row.paymentDate ? new Date(row.paymentDate).toLocaleDateString() : `Period ${row.period}`}</td>
                            <td>${row.payment.toFixed(2)}</td>
                            <td>${row.principal.toFixed(2)}</td>
                            <td>${row.interest.toFixed(2)}</td>
                            <td>${row.balance.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {loan.payments && loan.payments.length > 0 && (
                    <div className="payment-history-section">
                      <h4>Payment History</h4>
                      <div className="payment-list">
                        {loan.payments.map((payment, index) => (
                          <div key={index} className="payment-item">
                            <div className="payment-header">
                              <span className="payment-amount">${payment.amount.toFixed(2)}</span>
                              <span className="payment-date">{new Date(payment.date).toLocaleDateString()}</span>
                            </div>
                            {payment.notes && (
                              <div className="payment-notes">{payment.notes}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LoaneeLoans;
